import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

function Section({ title, children }) {
  return (
    <div className="border rounded-lg p-6 mb-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
      <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{title}</h2>
      <div className="space-y-3" style={{ color: '#b8a68f' }}>{children}</div>
    </div>
  );
}

const TOPICS = [
  { id: 'direct-referral', icon: '💰', en: 'Direct Referral', zh: '直推' },
  { id: 'monthly', icon: '📅', en: 'Monthly (Team Booster / Passive)', zh: '月度（团队激励 / 被动）' },
  { id: 'team-bonus', icon: '🌳', en: 'Team Bonus', zh: '团队奖金(对碰)' },
];

export default function AdminGuide() {
  const { user, loading: authLoading } = useAuth();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isZh = i18n.language === 'zh';
  const isAdmin = user && (user.role === 'admin' || user.role === 'superadmin');
  const [topic, setTopic] = useState('direct-referral');
  const [floatOpen, setFloatOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!(user.role === 'admin' || user.role === 'superadmin')) {
      navigate('/dashboard');
    }
  }, [authLoading, user, navigate]);

  if (!user || !isAdmin) {
    return null;
  }

  const selectTopic = (id) => {
    setTopic(id);
    setFloatOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const Bold = ({ children }) => <strong style={{ color: '#c9a961' }}>{children}</strong>;

  return (
    <div className="min-h-screen px-4 pt-24 pb-32 relative" style={{ backgroundColor: '#1a3a2a' }}>
    <div className="max-w-3xl mx-auto">
      <style>{`
        @page { size: portrait; }
        @media print {
          header, nav, footer, .no-print { display: none !important; }
          html, body { background: #1a3a2a !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
          .print-page { background: #1a3a2a !important; padding: 0 !important; margin: 0 !important; }
          .print-only { display: flex !important; }
        }
        .print-only { display: none; }
      `}</style>
      <div className="print-page">
      <div className="print-only items-center gap-2 mb-4">
        <img src="/images/wellis-logo.png" alt="Wellis" style={{ height: '48px', width: 'auto' }} />
        <span className="text-headline-md font-bold uppercase tracking-widest" style={{ color: '#c9a961' }}>WELLIS</span>
      </div>
      <h1 className="text-headline-lg font-bold mb-2" style={{ color: '#c9a961' }}>
        {isZh ? '管理员指南' : 'Admin Guide'}
      </h1>
      <p className="text-body-sm mb-6" style={{ color: '#999' }}>
        {isZh ? '直推、月度奖励与团队奖金(对碰)系统的管理员参考' : 'Admin reference for the Direct Referral, Monthly, and Team Bonus payout systems'}
      </p>

      {/* Topic tabs */}
      <div className="no-print flex gap-2 mb-6 border-b flex-wrap" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
        {TOPICS.map((t) => (
          <button
            key={t.id}
            onClick={() => selectTopic(t.id)}
            className="px-4 py-2 text-body-sm font-medium rounded-t-lg transition-colors"
            style={
              topic === t.id
                ? { color: '#0d2818', backgroundColor: '#c9a961' }
                : { color: '#c9a961', backgroundColor: 'transparent' }
            }
          >
            {t.icon} {isZh ? t.zh : t.en}
          </button>
        ))}
      </div>

      {/* ============================== DIRECT REFERRAL ============================== */}
      {topic === 'direct-referral' && (
      <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-headline-md font-bold" style={{ color: '#c9a961' }}>
          {isZh ? '直推' : 'Direct Referral'}
        </h2>
        <button
          onClick={() => window.print()}
          className="no-print text-body-sm font-medium px-4 py-2 rounded border hover:opacity-80 transition-opacity"
          style={{ color: '#0d2818', backgroundColor: '#c9a961', borderColor: '#c9a961' }}
        >
          {isZh ? '🖨️ 打印本指南' : '🖨️ Print this guide'}
        </button>
      </div>

      {isZh ? (
        <>
          <Section title="这是什么">
            <p>每当有会员确认购买配套（或被管理员标记为"预处理"），系统会自动向其<Bold>直接推荐人（第一级，8%）</Bold>和<Bold>推荐人的推荐人（第二级，5%）</Bold>各发放一笔佣金，按购买金额的百分比计算。</p>
          </Section>
          <Section title="操作流程">
            <ol className="list-decimal ml-6 space-y-1">
              <li>点击<Bold>计算 (Calculate)</Bold>——系统会扫描所有已确认的购买记录（包括预处理会员），为尚未生成佣金的购买生成新的待发放记录。已经生成过的购买不会重复生成。</li>
              <li>在<Bold>待发放 (Pending)</Bold> 标签页中勾选要发放的记录，点击<Bold>发放 (Release)</Bold>，积分即会计入对应会员的账户。</li>
              <li>已发放的记录会出现在<Bold>已发放 (Released)</Bold> 标签页，作为永久历史记录。</li>
            </ol>
          </Section>
          <Section title="排除功能 (Exclusions)">
            <p>如果某笔购买不应该产生佣金（例如测试账户），可在待发放或已标记记录上点击<Bold>排除 (Exclude)</Bold>——这会立即删除该笔购买产生的待发放佣金，并<Bold>永久阻止</Bold>该笔购买在未来的计算中再次生成佣金。在<Bold>排除列表 (Exclusions)</Bold> 标签页可以随时点击<Bold>重新纳入 (Re-include)</Bold> 撤销排除。</p>
          </Section>
          <Section title="⚠ 标记 (Flagged)">
            <p>每个配套可设置一个<Bold>单笔购买的累计被动收益上限</Bold>。一旦该笔购买累计已发放的被动收益（被动 / 直推合并计算）达到上限，之后针对该笔购买生成的新记录会自动标记为<Bold>⚠ 已标记</Bold>，不能被发放，需要管理员人工复核。</p>
          </Section>
        </>
      ) : (
        <>
          <Section title="What this is">
            <p>Whenever a member confirms a package purchase (or is marked "pre-processed" by an admin), the system automatically owes a commission to their <Bold>direct sponsor (Tier 1, 8%)</Bold> and their <Bold>sponsor's sponsor (Tier 2, 5%)</Bold>, calculated as a percentage of the purchase amount.</p>
          </Section>
          <Section title="How to use it">
            <ol className="list-decimal ml-6 space-y-1">
              <li>Click <Bold>Calculate</Bold> — this scans every confirmed purchase (including pre-processed members) and creates new pending commission rows for any purchase that doesn't already have one. Purchases already processed are never duplicated.</li>
              <li>On the <Bold>Pending</Bold> tab, check the rows to pay and click <Bold>Release</Bold> — the points are credited to the recipient's balance immediately.</li>
              <li>Released rows move to the <Bold>Released</Bold> tab as a permanent record.</li>
            </ol>
          </Section>
          <Section title="Exclusions">
            <p>If a specific purchase should never generate a commission (e.g. a test account), click <Bold>Exclude</Bold> on its pending or flagged row — this immediately deletes any pending commission from that purchase and <Bold>permanently stops</Bold> it from generating one again on future Calculates. Undo anytime from the <Bold>Exclusions</Bold> tab's <Bold>Re-include</Bold> button.</p>
          </Section>
          <Section title="⚠ Flagged rows">
            <p>Every package can have a <Bold>per-purchase lifetime passive payout cap</Bold> set. Once that specific purchase's cumulative released passive payout (combined across Passive and Direct Referral) reaches the cap, any further row generated for that purchase is automatically marked <Bold>⚠ Flagged</Bold> instead of pending — it can't be released until an admin manually reviews it.</p>
          </Section>
        </>
      )}
      </>
      )}

      {/* ============================== MONTHLY ============================== */}
      {topic === 'monthly' && (
      <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-headline-md font-bold" style={{ color: '#c9a961' }}>
          {isZh ? '月度奖励：团队激励 与 被动' : 'Monthly: Team Booster & Passive'}
        </h2>
        <button
          onClick={() => window.print()}
          className="no-print text-body-sm font-medium px-4 py-2 rounded border hover:opacity-80 transition-opacity"
          style={{ color: '#0d2818', backgroundColor: '#c9a961', borderColor: '#c9a961' }}
        >
          {isZh ? '🖨️ 打印本指南' : '🖨️ Print this guide'}
        </button>
      </div>

      {isZh ? (
        <>
          <Section title="/passive 页面包含两个完全独立的系统">
            <p><Bold>团队激励 (Team Booster)</Bold>——沿<Bold>推荐链</Bold>向上逐级分配的星级差额奖金。</p>
            <p><Bold>被动 (Passive)</Bold>——基于<Bold>累计股份</Bold>的持续分红池。</p>
            <p>两者使用不同的树（推荐链 vs. 累计购买份额）、不同的计算逻辑，互不影响。</p>
          </Section>

          <Section title="团队激励 (Team Booster) 如何计算">
            <p>每笔已确认购买会按<Bold>被动比例（默认 10%）</Bold>产生一个基础金额，然后沿购买者的<Bold>推荐人链</Bold>向上走：链条中每一位星级为 1-7 的推荐人，会拿走"剩余金额"中对应星级比例的份额（1★=20%，2★=30%，3★=40%，4★=50%，5★=60%，6★=80%，7★=100%），拿走后从剩余金额中扣除，再传给链条上更高一级的推荐人。</p>
            <p><Bold>同级或以下 10% 特例</Bold>：如果某位推荐人的星级<Bold>等于或低于购买者（下线）本人当前的星级</Bold>（即下线星级不低于该推荐人），该推荐人只拿固定 10%，而不是其星级对应的正常比例。</p>
            <p><Bold>Calculate</Bold> 只处理<Bold>尚未生成过任何记录</Bold>的购买——一旦某笔购买已生成记录，之后再点 Calculate 不会重新处理它，即使相关人员的星级后来发生了变化。</p>
            <p><Bold>顶部的 Recalculate 按钮</Bold>专门用于解决这个问题：它会刷新<Bold>所有</Bold>仍处于待发放状态、尚未发放过的记录，按当前最新的星级重新计算——这与 Calculate 是两个不同的操作，Calculate 只找"全新"的购买，Recalculate 只刷新"已存在但可能过时"的待发放记录。</p>
            <p>发放时按<Bold>现金积分 / 产品积分各 50%</Bold>拆分。</p>
          </Section>

          <Section title="被动 (Passive) 如何计算">
            <p>这是一个<Bold>终身累计</Bold>的分红系统：每一笔符合条件的购买（配套需设置了"分配权重"）在<Bold>首次</Bold>被 Calculate 扫描到时，会被"记入股份"（永久记录，之后不会重复记入）。</p>
            <p>顶部的<Bold>月份选择器只是给这一批计算贴一个标签</Bold>，<Bold>不是</Bold>按购买日期筛选——不管购买发生在哪个月，只要还没被记入过股份，点击 Calculate 时都会被纳入。</p>
            <p>每次 Calculate："本期新增销售额"（Total New Sales this Period）×分红比例（默认 10%）= 本期奖金池，再按所有历史股份的<Bold>权重</Bold>比例分给<Bold>所有</Bold>已记入股份的会员（不仅仅是本期新购买的人）。</p>
            <p>发放时按<Bold>现金积分 / 产品积分各 50%</Bold>拆分。</p>
          </Section>

          <Section title="共通操作">
            <p>两个系统都有<Bold>待发放 / 已发放 / 排除列表</Bold>三个标签页，操作方式与直推一致：勾选后 Release，或在排除列表中管理永久排除的对象。</p>
          </Section>
        </>
      ) : (
        <>
          <Section title="/passive hosts two completely separate systems">
            <p><Bold>Team Booster</Bold> — a star-differential bonus that cascades up the <Bold>sponsorship chain</Bold>.</p>
            <p><Bold>Passive</Bold> — a running dividend pool based on <Bold>cumulative shares</Bold>.</p>
            <p>They use different trees (sponsorship chain vs. cumulative purchase shares) and different math, and don't affect each other.</p>
          </Section>

          <Section title="How Team Booster is calculated">
            <p>Every confirmed purchase generates a base amount (price × the Passive rate, default 10%), then walks up the buyer's <Bold>sponsorship chain</Bold>: each sponsor in the chain who holds a star rank (1-7) takes their star's percentage of whatever's <Bold>left</Bold> (1★=20%, 2★=30%, 3★=40%, 4★=50%, 5★=60%, 6★=80%, 7★=100%), which is then deducted from the remaining pot before it continues up to the next sponsor.</p>
            <p><Bold>Same-rank-or-lower 10% override</Bold>: if a sponsor's own star rank is equal to or lower than the buyer's (downline's) current star rank — i.e. the downline outranks or matches the sponsor — that sponsor gets a flat 10% instead of their normal tier percentage.</p>
            <p><Bold>Calculate</Bold> only ever processes purchases that <Bold>have no record yet</Bold> — once a purchase has been calculated, clicking Calculate again never touches it again, even if someone's rank changes afterward.</p>
            <p>The <Bold>Recalculate button at the top</Bold> exists specifically for that: it refreshes <Bold>every</Bold> still-pending, not-yet-released record using everyone's current rank. Calculate finds brand-new purchases only; Recalculate refreshes existing-but-possibly-stale pending ones — they're deliberately two different actions.</p>
            <p>Released at a <Bold>50/50 Cash Points / Product Points</Bold> split.</p>
          </Section>

          <Section title="How Passive is calculated">
            <p>This is a <Bold>lifetime cumulative</Bold> dividend system: every eligible purchase (its package must have an allocation weight set) gets "tabulated" into a permanent share the <Bold>first</Bold> time Calculate sweeps it up — never again after that.</p>
            <p>The month picker at the top is <Bold>only a label</Bold> for that calculation batch — it does <Bold>not</Bold> filter by purchase date. Whatever hasn't been tabulated yet gets swept in whenever Calculate runs, regardless of which month the purchase actually happened in.</p>
            <p>Each Calculate: "Total New Sales this Period" × the distribution rate (default 10%) = that period's pool, split by weight among <Bold>every</Bold> share ever tabulated (not just this period's new buyers).</p>
            <p>Released at a <Bold>50/50 Cash Points / Product Points</Bold> split.</p>
          </Section>

          <Section title="Shared behavior">
            <p>Both systems share the same <Bold>Pending / Released / Exclusions</Bold> tab shape as Direct Referral — check rows and Release, or manage permanent exclusions from the Exclusions tab.</p>
          </Section>
        </>
      )}
      </>
      )}

      {/* ============================== TEAM BONUS ============================== */}
      {topic === 'team-bonus' && (
      <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-headline-md font-bold" style={{ color: '#c9a961' }}>
          {isZh ? '团队奖金(对碰)' : 'Team Bonus'}
        </h2>
        <button
          onClick={() => window.print()}
          className="no-print text-body-sm font-medium px-4 py-2 rounded border hover:opacity-80 transition-opacity"
          style={{ color: '#0d2818', backgroundColor: '#c9a961', borderColor: '#c9a961' }}
        >
          {isZh ? '🖨️ 打印本指南' : '🖨️ Print this guide'}
        </button>
      </div>

      {isZh ? (
        <>
          <Section title="这是什么">
            <p>一个基于<Bold>安置树（族谱）</Bold>——而非推荐链——的"弱枝配对"奖金。每位在安置树中拥有位置的会员最多有 <Bold>3 条分支</Bold>（左 / 中 / 右）。系统会取三条分支中<Bold>累计购买量最小的一条</Bold>作为配对基准，按<Bold>固定 10%</Bold> 比例发放奖金——<Bold>任何人</Bold>都有资格获得，不需要星级。</p>
          </Section>
          <Section title="关键规则">
            <p><Bold>至少需要 2 条分支有人</Bold>才可能配对——如果某条分支完全没有人被安置（不是"该分支金额为 0"，而是压根没有人在那条分支上），系统会忽略这条空分支，只在<Bold>已占用</Bold>的分支之间取最小值；但如果一条分支<Bold>已经有人安置</Bold>、只是还没有确认购买（金额为 0），这条分支仍会参与比较，并会因此阻止配对。</p>
            <p><Bold>终身累计</Bold>：与被动一样，月份只是批次标签，不按日期筛选——比较的是每条分支<Bold>历史至今的全部</Bold>确认购买总额。</p>
            <p><Bold>只有已发放的部分才会被消耗</Bold>：一笔"待发放"的配对金额<Bold>不会</Bold>锁定该分支的额度——只有点击"发放"之后，这笔金额才会真正从三条分支中同时扣除，之后才不能被重复配对。</p>
            <p><Bold>一批一旦有记录被发放，该批次即被冻结</Bold>：不能再对同一批次重新计算（避免同一份未消耗额度被两次不同批次同时占用）。请先发放或排除该批次的所有待发放记录，再计算下一批。</p>
            <p>发放时按<Bold>现金积分 / 产品积分各 50%</Bold>拆分。</p>
          </Section>
          <Section title="下钻查看（分支明细）">
            <p>点击表格中每条分支的金额，可展开查看该分支下具体是哪些人的哪些购买合计出这个数字——标注为"当前族谱"，因为这是<Bold>实时</Bold>按当前安置树重新计算的，如果之后有人被移动位置，这个数字会随之变化（历史记录本身保存的快照金额不会变）。</p>
          </Section>
        </>
      ) : (
        <>
          <Section title="What this is">
            <p>A "weak-leg" matching bonus based on the <Bold>placement tree</Bold> (genealogy) — not the sponsorship chain. Every member with a placement has up to <Bold>3 legs</Bold> (left / center / right). The system takes the <Bold>smallest</Bold> of the three legs' cumulative purchase volume as the match, and pays a flat <Bold>10%</Bold> on it — <Bold>anyone</Bold> is eligible, no star rank required.</p>
          </Section>
          <Section title="Key rules">
            <p><Bold>At least 2 legs must be occupied</Bold> for a match to be possible — if a leg has literally no one placed there (different from a leg with someone placed but $0 confirmed volume), it's excluded from the comparison entirely; but an <Bold>occupied</Bold> leg with $0 volume still counts and will correctly block a match.</p>
            <p><Bold>Lifetime cumulative</Bold>: same as Passive, the month picker is just a batch label, not a date filter — what's compared is each leg's <Bold>entire history</Bold> of confirmed purchases to date.</p>
            <p><Bold>Only released volume is consumed</Bold>: a pending match amount does <Bold>not</Bold> lock that volume out of future matching — only once you click Release does that amount actually get subtracted from all three legs, permanently.</p>
            <p><Bold>A batch freezes once anything in it is released</Bold> — you can't recalculate that same batch again afterward (this prevents the same unconsumed volume being claimed twice by two different batches). Release or exclude everything pending in a batch before calculating the next one.</p>
            <p>Released at a <Bold>50/50 Cash Points / Product Points</Bold> split.</p>
          </Section>
          <Section title="Drill-down (leg breakdown)">
            <p>Click any leg's volume figure in the table to expand exactly which purchases from which members add up to that number — labeled "Current genealogy" because it's recalculated <Bold>live</Bold> against the current placement tree; if someone is moved afterward, this figure changes accordingly (the historical record's own saved snapshot amount does not).</p>
          </Section>
        </>
      )}
      </>
      )}
      </div>
    </div>

      {/* Floating quick-switch tool */}
      <div className="no-print fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
        {floatOpen && (
          <div className="rounded-lg shadow-lg border overflow-hidden mb-1" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            {TOPICS.map((t) => (
              <button
                key={t.id}
                onClick={() => selectTopic(t.id)}
                className="block w-full text-left px-4 py-3 text-body-sm whitespace-nowrap transition-colors"
                style={
                  topic === t.id
                    ? { color: '#0d2818', backgroundColor: '#c9a961' }
                    : { color: '#c9a961', backgroundColor: 'transparent' }
                }
              >
                {t.icon} {isZh ? t.zh : t.en}
              </button>
            ))}
          </div>
        )}
        <button
          onClick={() => setFloatOpen(!floatOpen)}
          className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-2xl hover:opacity-90 transition-opacity"
          style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
          aria-label={isZh ? '快速切换指南' : 'Quick-switch guides'}
          title={isZh ? '快速切换指南' : 'Quick-switch guides'}
        >
          {floatOpen ? '✕' : '📚'}
        </button>
      </div>
    </div>
  );
}
